'use client'

import * as React from 'react'

import { cn } from '../cn'
import { ActionCard, type ActionCardTone } from './action-card'
import { Button } from './button'

/**
 * ChoiceCard — pick one (or N) from a small option set.
 *
 * The canonical "Pick a slot" surface from Anthropic's MCP demo. Uses a
 * simple grid of clickable option buttons rather than a radio-group
 * because the typical option count is 2-4 and the buttons read better
 * inside a tight chat column. Per
 * `apps/agent/plans/plan-action-cards.md` Phase 3.
 *
 * Mode `pickOne` (default) replaces the active selection on click;
 * `pickMany` toggles. Resolution fires when the user clicks Confirm
 * (or Cancel for the cancelled-kind resolution).
 */

export interface ChoiceOption {
  id: string
  label: string
  sublabel?: string
  detail?: string
  tone?: 'neutral' | 'recommended' | 'warning'
}

export interface ChoiceCardProps {
  title: string
  description?: string
  attribution?: string
  options: ChoiceOption[]
  mode?: 'pickOne' | 'pickMany'
  confirmLabel?: string
  cancelLabel?: string
  /** Initial selection — option ids. */
  defaultSelected?: string[]
  /** When the user resolves with a non-empty selection. */
  onConfirm?: (selectedIds: string[]) => void
  /** When the user dismisses (close button). */
  onCancel?: () => void
  /**
   * Once resolved, lock the card and dim non-selected options. Lets the
   * transcript preserve "what was picked" without offering re-pick.
   */
  resolvedSelectedIds?: string[]
}

const TONE_RING: Record<NonNullable<ChoiceOption['tone']>, string> = {
  neutral: 'border-border',
  recommended: 'border-emerald-300 dark:border-emerald-500/60',
  warning: 'border-amber-300 dark:border-amber-500/60',
}

export function ChoiceCard({
  title,
  description,
  attribution,
  options,
  mode = 'pickOne',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  defaultSelected = [],
  onConfirm,
  onCancel,
  resolvedSelectedIds,
}: ChoiceCardProps) {
  const resolved = resolvedSelectedIds !== undefined
  const [selected, setSelected] = React.useState<Set<string>>(
    () => new Set(resolved ? resolvedSelectedIds : defaultSelected),
  )

  const tone: ActionCardTone = resolved ? 'ok' : 'pending'

  const toggle = (id: string) => {
    if (resolved) {
      return
    }
    setSelected((prev) => {
      const next = new Set(prev)
      if (mode === 'pickOne') {
        next.clear()
        next.add(id)
      } else if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const canConfirm = !resolved && selected.size > 0

  return (
    <ActionCard
      title={title}
      description={description}
      attribution={attribution}
      tone={tone}
      footer={
        resolved ? null : (
          <>
            <Button type="button" size="sm" disabled={!canConfirm} onClick={() => onConfirm?.(Array.from(selected))}>
              {confirmLabel}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onCancel}>
              {cancelLabel}
            </Button>
          </>
        )
      }>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isSelected = selected.has(opt.id)
          const ringClass = TONE_RING[opt.tone ?? 'neutral']
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt.id)}
              disabled={resolved && !isSelected}
              className={cn(
                'flex min-w-[110px] flex-col items-start gap-0.5 rounded-md border bg-background px-3 py-2 text-left text-sm transition-colors',
                ringClass,
                isSelected && !resolved && 'border-foreground ring-2 ring-foreground/40',
                isSelected && resolved && 'border-emerald-500 ring-2 ring-emerald-500/40',
                !resolved && 'hover:bg-muted',
                resolved && !isSelected && 'opacity-40',
              )}
              aria-pressed={isSelected}>
              <span className="font-medium">{opt.label}</span>
              {opt.sublabel ? <span className="text-xs text-muted-foreground">{opt.sublabel}</span> : null}
              {opt.detail && isSelected ? (
                <span className="mt-1 text-xs text-muted-foreground">{opt.detail}</span>
              ) : null}
            </button>
          )
        })}
      </div>
    </ActionCard>
  )
}
